/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFocusEffect, useNavigation, useRoute, type ParamListBase } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import {
  listVehicleServiceReminders,
  upsertServiceReminder,
  completeServiceReminder,
  snoozeServiceReminder,
  deleteServiceReminder,
  type ServiceReminder,
} from '../infra/serviceRemindersRepo';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { showSuccess, showError } from '../../shared/ui/appToast'
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';

type ServiceRemindersRouteParams = {
  vehicleId?: string;
  vehicleLabel?: string;
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function ServiceRemindersScreen() {
  const navigation = useNavigation<NativeStackNavigationProp<ParamListBase>>();
  const route = useRoute();
  const params = route.params as ServiceRemindersRouteParams | undefined;
  const vehicleId = params?.vehicleId ?? '';
  const vehicleLabel = params?.vehicleLabel ?? 'Vehículo';
  const { entitlements } = usePlanEntitlements();
  const organizationId = entitlements?.organization_id ?? null;
  const capabilityGate = useCapabilityGate('work_orders', {
    title: 'Vehículos no disponibles',
    subtitle: 'Este rubro no tiene órdenes de trabajo.',
  });

  const [reminders, setReminders] = useState<ServiceReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { confirm, ConfirmModal } = useOperationalConfirm();

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [label, setLabel] = useState('');
  const [triggerKm, setTriggerKm] = useState('');
  const [triggerDays, setTriggerDays] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!vehicleId) {
      setError('Falta el ID del vehículo.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await listVehicleServiceReminders(vehicleId, true);
      setReminders(data);
    } catch (e: any) {
      setError(e?.message ?? 'Error al cargar recordatorios.');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const resetForm = () => {
    setLabel('');
    setTriggerKm('');
    setTriggerDays('');
    setNotes('');
    setShowForm(false);
  };

  const handleSave = useCallback(async () => {
    if (!organizationId || !vehicleId) {
      showError('Error', 'Falta información de organización o vehículo.');
      return;
    }
    if (!label.trim()) {
      showError('Revisá los datos', 'El label es obligatorio.');
      return;
    }
    const kmRaw = triggerKm.trim();
    const daysRaw = triggerDays.trim();
    const km = kmRaw ? parseInt(kmRaw, 10) : null;
    const days = daysRaw ? parseInt(daysRaw, 10) : null;
    if ((km != null && !Number.isFinite(km)) || (days != null && !Number.isFinite(days))) {
      showError('Revisá los datos', 'Los valores de km y días deben ser números enteros.');
      return;
    }
    if (km == null && days == null) {
      showError('Revisá los datos', 'Especificá al menos un trigger: km o días.');
      return;
    }
    setSaving(true);
    try {
      await upsertServiceReminder(organizationId, {
        vehicleId,
        label: label.trim(),
        triggerKm: km,
        triggerDays: days,
        notes: notes.trim() || null,
      });
      resetForm();
      showSuccess('Recordatorio guardado', '');
      await load();
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'vehicles', op: 'service_reminder.save', error: e, userMessage: 'No se pudo guardar el recordatorio.' });
    } finally {
      setSaving(false);
    }
  }, [organizationId, vehicleId, label, triggerKm, triggerDays, notes, load]);

  const handleComplete = useCallback(
    async (reminder: ServiceReminder) => {
      const ok = await confirm({
        title: 'Marcar como completado',
        message: `¿Querés marcar "${reminder.label}" como completado hoy?`,
        confirmLabel: 'Completar',
      });
      if (!ok) return;
      try {
        await completeServiceReminder(reminder.id);
        await load();
      } catch (e: unknown) {
        presentUnexpectedFailure({ module: 'vehicles', op: 'service_reminder.complete', error: e, userMessage: 'No se pudo completar.' });
      }
    },
    [load, confirm],
  );

  const handleSnooze = useCallback(
    async (reminder: ServiceReminder) => {
      const ok30 = await confirm({
        title: 'Posponer',
        message: '¿Querés posponer 30 días?',
        confirmLabel: '30 días',
      });
      if (ok30) {
        try {
          await snoozeServiceReminder(reminder.id, 30);
          await load();
        } catch (e: unknown) {
          presentUnexpectedFailure({ module: 'vehicles', op: 'service_reminder.snooze', error: e, userMessage: 'No se pudo posponer.' });
        }
        return;
      }
      const ok90 = await confirm({
        title: 'Posponer',
        message: '¿Querés posponer 90 días?',
        confirmLabel: '90 días',
      });
      if (!ok90) return;
      try {
        await snoozeServiceReminder(reminder.id, 90);
        await load();
      } catch (e: unknown) {
        presentUnexpectedFailure({ module: 'vehicles', op: 'service_reminder.snooze', error: e, userMessage: 'No se pudo posponer.' });
      }
    },
    [load, confirm],
  );

  const handleDelete = useCallback(
    async (reminder: ServiceReminder) => {
      const ok = await confirm({
        title: 'Eliminar',
        message: `¿Seguro que querés eliminar "${reminder.label}"?`,
        confirmLabel: 'Eliminar',
        destructive: true,
      });
      if (!ok) return;
      try {
        await deleteServiceReminder(reminder.id);
        await load();
      } catch (e: unknown) {
        presentUnexpectedFailure({ module: 'vehicles', op: 'service_reminder.delete', error: e, userMessage: 'No se pudo eliminar.' });
      }
    },
    [load, confirm],
  );

  if (!capabilityGate.allowed) {
    return <ShellContentFrame>{capabilityGate.blocked}</ShellContentFrame>;
  }

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#2563eb" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={() => void load()}>
          <Text style={styles.retryText}>Reintentar</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <>
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>Recordatorios de servicio</Text>
      <Text style={styles.subtitle}>{vehicleLabel}</Text>

      {reminders.length === 0 && !showForm ? (
        <Text style={styles.empty}>No hay recordatorios. Agregá el primero.</Text>
      ) : (
        reminders.map((r) => {
          const isSnoozed = r.snoozedUntil && new Date(r.snoozedUntil) > new Date();
          return (
            <View key={r.id} style={[styles.reminderCard, !r.isActive && styles.reminderInactive]}>
              <View style={styles.reminderHeader}>
                <Text style={styles.reminderLabel}>{r.label}</Text>
                {isSnoozed ? (
                  <Text style={styles.snoozedBadge}>Pospuesto</Text>
                ) : !r.isActive ? (
                  <Text style={styles.inactiveBadge}>Inactivo</Text>
                ) : null}
              </View>
              <View style={styles.reminderMeta}>
                {r.triggerKm != null ? (
                  <Text style={styles.metaText}>Cada {r.triggerKm.toLocaleString('es-AR')} km</Text>
                ) : null}
                {r.triggerDays != null ? (
                  <Text style={styles.metaText}>Cada {r.triggerDays} días</Text>
                ) : null}
              </View>
              {r.lastCompletedAt ? (
                <Text style={styles.lastCompleted}>
                  Último: {formatDate(r.lastCompletedAt)}
                  {r.lastCompletedKm != null
                    ? ` · ${r.lastCompletedKm.toLocaleString('es-AR')} km`
                    : ''}
                </Text>
              ) : null}
              {r.notes ? <Text style={styles.reminderNotes}>{r.notes}</Text> : null}
              {r.isActive ? (
                <View style={styles.reminderActions}>
                  <TouchableOpacity
                    style={styles.completeBtn}
                    onPress={() => void handleComplete(r)}
                  >
                    <Text style={styles.completeBtnText}>Completar</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.snoozeBtn}
                    onPress={() => void handleSnooze(r)}
                  >
                    <Text style={styles.snoozeBtnText}>Posponer</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.deleteBtn}
                    onPress={() => void handleDelete(r)}
                  >
                    <Text style={styles.deleteBtnText}>Eliminar</Text>
                  </TouchableOpacity>
                </View>
              ) : null}
            </View>
          );
        })
      )}

      {showForm ? (
        <View style={styles.formCard}>
          <Text style={styles.formTitle}>Nuevo recordatorio</Text>
          <Text style={styles.label}>Descripción *</Text>
          <TextInput
            style={styles.input}
            value={label}
            onChangeText={setLabel}
            placeholder="Ej: Cambio de aceite"
            placeholderTextColor="#9ca3af"
          />
          <Text style={styles.label}>Cada cuántos km</Text>
          <TextInput
            style={styles.input}
            value={triggerKm}
            onChangeText={setTriggerKm}
            placeholder="Ej: 10000"
            placeholderTextColor="#9ca3af"
            keyboardType="numeric"
          />
          <Text style={styles.label}>Cada cuántos días</Text>
          <TextInput
            style={styles.input}
            value={triggerDays}
            onChangeText={setTriggerDays}
            placeholder="Ej: 180"
            placeholderTextColor="#9ca3af"
            keyboardType="numeric"
          />
          <Text style={styles.label}>Notas (opcional)</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={notes}
            onChangeText={setNotes}
            placeholder="Notas internas"
            placeholderTextColor="#9ca3af"
            multiline
            numberOfLines={3}
          />
          <View style={styles.formActions}>
            <TouchableOpacity style={styles.cancelBtn} onPress={resetForm}>
              <Text style={styles.cancelBtnText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.saveBtn}
              onPress={() => void handleSave()}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.saveBtnText}>Guardar</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      ) : (
        <TouchableOpacity style={styles.addBtn} onPress={() => setShowForm(true)}>
          <Text style={styles.addBtnText}>+ Agregar recordatorio</Text>
        </TouchableOpacity>
      )}
    </ScrollView>
    {ConfirmModal}
    </>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f9fafb' },
  content: { padding: 16, paddingBottom: 32 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 24 },
  title: { fontSize: 22, fontWeight: '700', color: '#111827', marginBottom: 4 },
  subtitle: { fontSize: 14, color: '#6b7280', marginBottom: 16 },
  empty: { textAlign: 'center', color: '#6b7280', fontSize: 14, paddingVertical: 24 },
  reminderCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 14,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  reminderInactive: { opacity: 0.6 },
  reminderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  reminderLabel: { fontSize: 16, fontWeight: '600', color: '#111827', flex: 1 },
  snoozedBadge: {
    fontSize: 11,
    color: '#ea580c',
    backgroundColor: '#fff7ed',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    fontWeight: '600',
  },
  inactiveBadge: {
    fontSize: 11,
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    fontWeight: '600',
  },
  reminderMeta: { flexDirection: 'row', gap: 12, marginBottom: 4 },
  metaText: { fontSize: 13, color: '#374151' },
  lastCompleted: { fontSize: 12, color: '#6b7280', marginBottom: 4 },
  reminderNotes: { fontSize: 13, color: '#4b5563', fontStyle: 'italic' },
  reminderActions: { flexDirection: 'row', gap: 8, marginTop: 8 },
  completeBtn: { backgroundColor: '#16a34a', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6 },
  completeBtnText: { color: '#fff', fontSize: 12, fontWeight: '600' },
  snoozeBtn: { backgroundColor: '#fff7ed', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6 },
  snoozeBtnText: { color: '#ea580c', fontSize: 12, fontWeight: '600' },
  deleteBtn: { backgroundColor: '#fee2e2', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6 },
  deleteBtnText: { color: '#dc2626', fontSize: 12, fontWeight: '600' },
  addBtn: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 8,
  },
  addBtnText: { color: '#2563eb', fontSize: 14, fontWeight: '600' },
  formCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 16,
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  formTitle: { fontSize: 16, fontWeight: '700', color: '#111827', marginBottom: 12 },
  label: { fontSize: 13, color: '#374151', fontWeight: '500', marginBottom: 4, marginTop: 8 },
  input: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: '#111827',
  },
  textArea: { minHeight: 60, textAlignVertical: 'top' },
  formActions: { flexDirection: 'row', gap: 8, marginTop: 16 },
  cancelBtn: { flex: 1, backgroundColor: '#f3f4f6', paddingVertical: 10, borderRadius: 8, alignItems: 'center' },
  cancelBtnText: { color: '#6b7280', fontSize: 14, fontWeight: '600' },
  saveBtn: { flex: 1, backgroundColor: '#2563eb', paddingVertical: 10, borderRadius: 8, alignItems: 'center' },
  saveBtnText: { color: '#fff', fontSize: 14, fontWeight: '600' },
  errorText: { color: '#dc2626', fontSize: 14, marginBottom: 12, textAlign: 'center' },
  retryButton: { backgroundColor: '#2563eb', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 8 },
  retryText: { color: '#fff', fontWeight: '600' },
});
